import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Award, Phone, ShieldCheck, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { MediaSection } from "@/components/MediaSection";
import { practices } from "@/data/practices";
import { supabase } from "@/integrations/supabase/client";
import { useLangPath } from "@/i18n/LangLayout";
import heroImg from "@/assets/hero-office.webp";
import attorneyImg from "@/assets/attorney-portrait.webp";

interface Testimonial { id: string; name: string; content: string; rating: number }

const Index = () => {
  const { t } = useTranslation();
  const lp = useLangPath();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    supabase.from("testimonials").select("id,name,content,rating").eq("published", true).then(({ data }) => {
      if (data?.length) setTestimonials(data);
    });
  }, []);

  const SITE_URL = "https://assi.org.il";
  const legalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${SITE_URL}/#legalservice`,
    name: t("common.siteName"),
    description: t("home.seoDesc"),
    url: SITE_URL,
    telephone: "+972-50-538-0626",
    email: "nizar@assi.org.il",
    address: { "@type": "PostalAddress", streetAddress: "רח׳ הסדנה", addressLocality: "כפר סבא", addressCountry: "IL" },
    areaServed: { "@type": "Country", name: "Israel" },
    priceRange: "₪₪",
    aggregateRating: testimonials.length > 0 ? {
      "@type": "AggregateRating",
      ratingValue: (testimonials.reduce((s, t) => s + (t.rating || 5), 0) / testimonials.length).toFixed(1),
      reviewCount: testimonials.length,
      bestRating: 5,
    } : undefined,
  };

  const stats = t("home.stats", { returnObjects: true }) as { cases: string; success: string; experience: string };
  const whyItems = t("home.why", { returnObjects: true }) as { t: string; d: string }[];
  const whyIcons = [ShieldCheck, Users, Award, Star, ShieldCheck];

  return (
    <>
      <SEO title={t("home.seoTitle")} description={t("home.seoDesc")} schema={legalServiceSchema} />

      <section className="relative min-h-[90vh] lg:min-h-[100vh] flex items-center overflow-hidden">
        <img src={heroImg} alt={t("common.siteName")} width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 gradient-hero opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-transparent" />

        <div className="container relative z-10 pt-24 pb-10 sm:pb-16 text-primary-foreground">
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-8 lg:gap-12 items-center">
            <div className="flex justify-center lg:justify-start lg:order-2">
              <img src={attorneyImg} alt={t("common.siteName")} width={1024} height={1024} loading="eager" fetchPriority="high" decoding="async"
                className="w-64 sm:w-80 md:w-96 lg:w-full lg:max-w-[34rem] drop-shadow-2xl fade-in-up" />
            </div>

            <div className="text-center lg:text-start lg:order-1">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/15 border border-accent/30 text-accent text-sm font-medium mb-6 fade-in-up">
                <Award className="w-4 h-4" />
                {t("common.yearsExperience")}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-black leading-tight mb-6 fade-in-up text-white" style={{ animationDelay: "100ms" }}>
                {t("home.heroTitle")}
                <span className="block text-white mt-2">{t("home.heroSubtitle")}</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-primary-foreground/85 leading-relaxed mb-8 fade-in-up" style={{ animationDelay: "200ms" }}>
                {t("home.heroDescription")}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3 sm:gap-4 fade-in-up" style={{ animationDelay: "300ms" }}>
                <Link to={lp("/contact")} className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto gradient-gold text-accent-foreground border-0 shadow-gold hover:opacity-90 h-14 px-8 text-base font-semibold">
                    {t("home.ctaConsult")}
                    <ArrowLeft className="w-5 h-5 ms-1" />
                  </Button>
                </Link>
                <a href="tel:0505380626">
                  <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-primary h-14 px-6 sm:px-8 text-base">
                    <Phone className="w-5 h-5 me-2" />
                    050-538-0626
                  </Button>
                </a>
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6 sm:mt-12 max-w-xl mx-auto lg:mx-0 fade-in-up" style={{ animationDelay: "400ms" }}>
                {[
                  { n: "1500+", l: stats.cases },
                  { n: "95%", l: stats.success },
                  { n: "15+", l: stats.experience },
                ].map(s => (
                  <div key={s.l} className="text-center border-e border-white/15 pe-2 sm:pe-4 last:border-e-0 last:pe-0">
                    <div className="font-heading text-2xl sm:text-3xl font-bold text-accent" dir="ltr">{s.n}</div>
                    <div className="text-xs sm:text-sm text-white/70">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 bg-secondary/30">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
              <div className="text-accent font-semibold text-sm tracking-wider uppercase mb-3">{t("home.practiceLabel")}</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">{t("home.practiceTitle")}</h2>
              <p className="text-muted-foreground text-lg">{t("home.practiceSub")}</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {practices.map((p, i) => (
              <Reveal key={p.slug} delay={i * 100}>
                <Link to={lp(`/practice/${p.slug}`)} aria-label={t(`practices.${p.i18nKey}.title`)}>
                  <Card className="group h-full p-7 hover:shadow-elegant transition-smooth border hover:border-accent/50 hover:-translate-y-1 cursor-pointer flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-xl gradient-gold flex items-center justify-center mb-5 shadow-gold group-hover:scale-110 transition-smooth">
                      <p.icon className="w-7 h-7 text-accent-foreground" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2">{t(`practices.${p.i18nKey}.title`)}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{t(`practices.${p.i18nKey}.short`)}</p>
                    <div className="text-accent font-medium text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 group-hover:gap-2 transition-all mt-auto">
                      {t("common.readMore")} <ArrowLeft className="w-4 h-4" />
                    </div>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MediaSection />

      <section className="py-14 sm:py-20 lg:py-24">
        <div className="container grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <Reveal>
            <div>
              <div className="text-accent font-semibold text-sm tracking-wider uppercase mb-3">{t("home.whyLabel")}</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">{t("home.whyTitle")}</h2>
              <div className="space-y-5">
                {whyItems.map((item, idx) => {
                  const Icon = whyIcons[idx] || ShieldCheck;
                  return (
                    <div key={item.t} className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-accent-soft flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg mb-1">{item.t}</h3>
                        <p className="text-muted-foreground text-sm">{item.d}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full rounded-2xl gradient-gold opacity-30 blur-2xl" />
              <Card className="relative p-8 shadow-elegant bg-card">
                <h3 className="font-heading text-2xl font-bold mb-2">{t("home.quickCheckTitle")}</h3>
                <p className="text-muted-foreground text-sm mb-6">{t("home.quickCheckSub")}</p>
                <LeadForm compact />
              </Card>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24">
        <div className="container">
          <Reveal>
            <div className="relative rounded-3xl gradient-hero p-8 sm:p-12 lg:p-16 text-center overflow-hidden shadow-elegant">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, hsl(var(--accent)) 0%, transparent 40%)" }} />
              <div className="relative">
                <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white mb-4">{t("home.ctaTitle")}</h2>
                <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t("home.ctaSub")}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to={lp("/contact")}>
                    <Button size="lg" className="gradient-gold text-accent-foreground border-0 shadow-gold hover:opacity-90 h-14 px-10 text-base font-semibold">
                      {t("home.ctaApply")}
                    </Button>
                  </Link>
                  <a href="https://wa.me/972505380626" target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-primary h-14 px-10 text-base">
                      {t("common.whatsapp")}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Index;
